import { useMemo } from 'react';
import type { GroupedScheduleResponse, BookingResponse } from '@/features/booking/types/bookingTypes';
import type {
    BookingTransfer,
    TransferDisplayInfo,
    UseTransferListReturn
} from '@/features/booking/types/transferTypes';
import { formatShortDate, formatTimeReadable, formatShortDateTime } from '@/lib/utils';

export function useTransferList(
    groupedSchedules: GroupedScheduleResponse[],
    allTransfers: BookingTransfer[]
): UseTransferListReturn {

    // Build transfer details map
    const transferDetails = useMemo(() => {
        const details: Record<string, BookingTransfer> = {};
        allTransfers.forEach(transfer => {
            details[transfer.id] = transfer;
        });
        return details;
    }, [allTransfers]);

    // Flatten transfer bookings from schedules (for richer event info)
    const transferBookingsFromSchedules = useMemo(() => {
        return groupedSchedules.flatMap(schedule =>
            schedule.bookings
                .filter(item => item.booking.transfer_id)
                .map(item => ({
                    booking: item.booking,
                    transferId: item.booking.transfer_id || '',
                    eventName: schedule.event_schedule.event?.name || 'Event',
                    eventDescription: schedule.event_schedule.event?.description || '',
                    eventDate: schedule.event_schedule.start_time,
                    eventImageId: schedule.event_schedule.event?.card_image_id || null,
                    businessName: schedule.event_schedule.event?.business?.name || '',
                    businessLocation: schedule.event_schedule.event?.business?.location?.address_line1 || '',
                    status: item.booking.status
                }))
        );
    }, [groupedSchedules]);

    // Transform transfers into display items
    const transferBookingItems = useMemo(() => {
        return allTransfers.map(transfer => {
            const scheduleItem = transferBookingsFromSchedules.find(
                item => item.booking.transfer_id === transfer.id
            );

            const transferData = transfer as any;
            const booking = transferData.booking;
            const event = transferData.event ||
                booking?.event ||
                booking?.event_booking_category?.event_schedule?.event;
            const eventSchedule = booking?.event_booking_category?.event_schedule;

            return {
                booking: (booking || { id: '', transfer_id: transfer.id }) as BookingResponse,
                transferId: transfer.id,
                eventName: scheduleItem?.eventName || event?.name || 'Event',
                eventDescription: scheduleItem?.eventDescription || event?.description || '',
                eventDate: scheduleItem?.eventDate || eventSchedule?.start_time || event?.start_time || transfer.created_at,
                eventImageId: scheduleItem?.eventImageId || event?.card_image_id || event?.image_id || null,
                businessName: scheduleItem?.businessName || event?.business?.name || '',
                businessLocation: scheduleItem?.businessLocation || event?.business?.location?.address_line1 || '',
                status: booking?.status || transferData.status || 'unknown'
            };
        });
    }, [allTransfers, transferBookingsFromSchedules]);

    // Sort by event date (newest first)
    const sortedBookings = useMemo(() => {
        return [...transferBookingItems].sort((a, b) =>
            new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime()
        );
    }, [transferBookingItems]);

    // Get display info for a transfer
    const getTransferDisplayInfo = (transferId: string): TransferDisplayInfo => {
        const transfer = transferDetails[transferId];
        const status = transfer?.status || 'PENDING';
        const isExpired = status === 'PENDING' &&
            Boolean(transfer?.expires_at) &&
            new Date(transfer.expires_at) < new Date();
        const displayStatus = isExpired ? 'EXPIRED' : status;
        const isPending = displayStatus === 'PENDING';

        return {
            displayStatus,
            isPending,
            isExpired,
            expiresAt: transfer?.expires_at || null
        };
    };

    return {
        sortedBookings,
        transferDetails,
        getTransferDisplayInfo,
        formatDate: formatShortDate,
        formatTime: formatTimeReadable,
        formatExpiresAt: formatShortDateTime,
    };
}
